import { FC, useMemo } from 'react'

import { Button, ButtonVariants } from '@inno-ui/button'
import {
  DashboardWidget,
  DashboardWidgetColorScheme
} from '@inno-ui/dashboard-widget'
import { ArrowRightIcon } from '@inno-ui/icons'
import { useQueryClient } from '@tanstack/react-query'
import { PLATFORM_NAMES } from 'modules/download-center/components/PlatformIcon'
import { detectPlatformOs } from 'modules/download-center/utils/detectPlatformOs'
import { getAppIconFallback } from 'modules/download-center/utils/imageFallback'
import { pickLocalizedText } from 'modules/download-center/utils/pickLocalizedText'
import { startDownload } from 'modules/download-center/utils/startDownload'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  DownloadableApp,
  GET_DOWNLOADABLE_APP_QUERY_KEY
} from 'shared/api/containerApi'

import styles from './AppCard.module.scss'

interface Props {
  app: DownloadableApp
}

const AppIcon: FC<{ src: string }> = ({ src }) => (
  <img src={src} alt='' className={styles.icon} />
)

export const AppCard: FC<Props> = ({ app }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const iconSrc = app.icon || getAppIconFallback(app.name)
  const AppIconComponent = useMemo(
    () => (() => <AppIcon src={iconSrc} />) as FC,
    [iconSrc]
  )

  const goToDetails = () => {
    queryClient.setQueryData([GET_DOWNLOADABLE_APP_QUERY_KEY, app.slug], app)
    navigate(`/download/${app.slug}`)
  }

  const handleDownload = () => {
    if (!app.platforms.length) return

    const detectedOs = detectPlatformOs()
    const platform =
      app.platforms.find(p => p.os === detectedOs) ?? app.platforms[0]

    startDownload(platform.url)
  }

  return (
    <DashboardWidget
      width='28.125rem'
      colorScheme={DashboardWidgetColorScheme.White}
      onClick={goToDetails}
      actionLabel={{
        label: t('downloadCenter.listPage.details'),
        trailingIcon: ArrowRightIcon
      }}
      header={{
        leadingIcon: { icon: AppIconComponent },
        title: app.name
      }}
    >
      <p className={styles.description}>
        {pickLocalizedText(app.description, i18n.language)}
      </p>

      <div className={styles.platforms}>
        {app.platforms.map(platform => PLATFORM_NAMES[platform.os]).join(', ')}
      </div>

      <div className={styles.footer}>
        <Button
          label={t('downloadCenter.downloadButton.download')}
          variant={ButtonVariants.Dark}
          disabled={!app.platforms.length}
          onClick={e => {
            e.stopPropagation()
            handleDownload()
          }}
        />
      </div>
    </DashboardWidget>
  )
}
